import type { Column } from "@shared/types/data-table"
import type { Ecommerce } from "@features/ecommerce/types/ecommerce.types"
import type { ColumnManagementConfig } from "@shared/lib/tables/utils/column-management"
import { 
  createCurrencyColumn, 
  createStatusColumn, 
  createDateColumn,
  createTruncatedColumn,
  createCustomColumn
} from "./column-factories"

export const ECOMMERCE_COLUMNS: Record<string, Column<Ecommerce>> = {
  name: createTruncatedColumn<Ecommerce>(
    "name",
    "Name",
    200,
    (ecommerce) => ecommerce.name,
    "font-bold"
  ),
  status: createStatusColumn<Ecommerce>("ecommerce"),
  type: createTruncatedColumn<Ecommerce>(
    "type",
    "Type",
    140,
    (ecommerce) => ecommerce.type
  ),
  description: createTruncatedColumn<Ecommerce>(
    "description",
    "Description",
    200,
    (ecommerce) => ecommerce.description
  ),
  revenue30d: createCurrencyColumn<Ecommerce>("revenue30d", "Revenue (30d)"),
  success: createCustomColumn<Ecommerce>(
    "success",
    "Success",
    120,
    (ecommerce: Ecommerce) => {
      const rate = ecommerce.success || 0
      return (
        <span className="font-medium">{rate}%</span>
      )
    }
  ),
  lastUsed: createDateColumn<Ecommerce>("lastUsed", "Last used", 150),
  created: createDateColumn<Ecommerce>("created", "Created", 150),
  lastUpdated: createDateColumn<Ecommerce>("lastUpdated", "Last updated", 150),
}

export const ECOMMERCE_STATUS_CONFIG = {
  all: ["name", "status", "type", "description", "revenue30d", "success", "lastUsed", "created", "lastUpdated"],
  active: ["name", "status", "type", "description", "revenue30d", "success", "lastUsed", "created", "lastUpdated"],
  inactive: ["name", "status", "type", "description", "created", "lastUpdated"],
} as const

export const getEcommerceColumns = (status: string = 'all'): Column<Ecommerce>[] => {
  const keys = ECOMMERCE_STATUS_CONFIG[status as keyof typeof ECOMMERCE_STATUS_CONFIG] || ECOMMERCE_STATUS_CONFIG.all
  return keys.map(key => ECOMMERCE_COLUMNS[key]).filter(Boolean)
}

export const getEcommerceColumnConfig = (status: string = 'all'): ColumnManagementConfig[] => {
  const visibleKeys: Set<string> = new Set(ECOMMERCE_STATUS_CONFIG[status as keyof typeof ECOMMERCE_STATUS_CONFIG] || ECOMMERCE_STATUS_CONFIG.all)
  return Object.entries(ECOMMERCE_COLUMNS).map(([key, col], index) => ({
    id: col.key,
    label: typeof col.header === 'string' ? col.header : col.key,
    visible: visibleKeys.has(key),
    required: ["name", "status"].includes(key),
    order: index
  }))
}